import actionConstants from '../constants/';

// Default filter state
const defaultFilters = {
  predefinedDateFilter: '7d',
  customDateRange: null,
  cbStatusFilter: '',
  searchText: '',
  searchCriteria: { label: 'Dispute ID', value: 'disputeId' },
  sortBy: 'chargebackDate',
  orderBy: 'DESC',
  page: 1,
  isActionDrawerOpen: false,
  selectedDisputeId: null as string | null,
  pageSize: 20,
};

const disputeListState = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  data: {
    items: [],
    totalChargebacks: 0,
    totalChargebackAmount: 0,
    totalDisputesNotResolved: 0,
    totalDisputesNotResolvedAmount: 0,
  },
  errorMessage: '',
};

const disputeDetailsState = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  data: null,
  errorMessage: '',
};

const disputeOrderDetailsV3State = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  data: null,
  errorMessage: '',
};

const disputeOrderListV3State = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  data: null,
  errorMessage: '',
};

const disputeDocumentConfigState = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  data: null,
  errorMessage: '',
};

const chargebackDefendModalState = {
  visible: false,
  type: '',
  continue: false,
};

const initialState = {
  disputeListState: { ...disputeListState },
  disputeDetailsState: { ...disputeDetailsState },
  disputeOrderDetailsV3State: { ...disputeOrderDetailsV3State },
  disputeOrderListV3State: { ...disputeOrderListV3State },
  disputeDocumentConfigState: { ...disputeDocumentConfigState },
  filters: { ...defaultFilters }, // Add filters to Redux state
  chargebackDefendModal: { ...chargebackDefendModalState },
  uploadDocumentState: {},
  reuploadDocumentState: {},
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function disputesReducer(state = initialState, action: any) {
  const { type, payload } = action;
  switch (type) {
    // Get Dispute List
    case actionConstants.GET_DISPUTE_LIST_REQUEST:
      return {
        ...state,
        disputeListState: {
          ...state.disputeListState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };

    case actionConstants.GET_DISPUTE_LIST_SUCCESS:
      return {
        ...state,
        disputeListState: {
          ...state.disputeListState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: {
            items: action.payload?.body?.data || [],
            totalChargebacks: action.payload?.body?.totalChargebacks || 0,
            totalChargebackAmount: action.payload?.body?.totalChargebackAmount || 0,
            totalDisputesNotResolved: action.payload?.body?.totalDisputesNotResolved || 0,
            totalDisputesNotResolvedAmount: action.payload?.body?.totalDisputesNotResolvedAmount || 0,
          },
        },
      };

    case actionConstants.GET_DISPUTE_LIST_FAILURE:
      return {
        ...state,
        disputeListState: {
          ...state.disputeListState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorMessage: payload?.body?.message || 'Failed to fetch disputes',
          errorCode: payload?.body?.errorCode || 0,
        },
      };

    case actionConstants.GET_DISPUTE_LIST_CLEAR:
      return {
        ...state,
        disputeListState,
      };

    // Get Dispute Details
    case actionConstants.GET_DISPUTE_DETAILS_REQUEST:
      return {
        ...state,
        disputeDetailsState: {
          ...state.disputeDetailsState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };

    case actionConstants.GET_DISPUTE_DETAILS_SUCCESS:
      return {
        ...state,
        disputeDetailsState: {
          ...state.disputeDetailsState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: payload?.body?.data || null,
        },
      };

    case actionConstants.GET_DISPUTE_DETAILS_FAILURE:
      return {
        ...state,
        disputeDetailsState: {
          ...state.disputeDetailsState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorMessage:
            payload?.body?.message || 'Failed to fetch dispute details',
          errorCode: payload?.body?.errorCode || 0,
          data: null
        },
        filters: {
          ...state.filters,
          selectedDisputeId: null
        },
      };

    case actionConstants.GET_DISPUTE_DETAILS_CLEAR:
      return {
        ...state,
        disputeDetailsState,
      };

    // Get Dispute Order Details V3
    case actionConstants.GET_DISPUTE_ORDER_DETAILS_V3_REQUEST:
      return {
        ...state,
        disputeOrderDetailsV3State: {
          ...state.disputeOrderDetailsV3State,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
          data: null
        },
      };

    case actionConstants.GET_DISPUTE_ORDER_DETAILS_V3_SUCCESS:
      return {
        ...state,
        disputeOrderDetailsV3State: {
          ...state.disputeOrderDetailsV3State,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: payload?.body?.data || null,
        },
      };

    case actionConstants.GET_DISPUTE_ORDER_DETAILS_V3_FAILURE:
      return {
        ...state,
        disputeOrderDetailsV3State: {
          ...state.disputeOrderDetailsV3State,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorMessage:
            payload?.body?.message || 'Failed to fetch dispute order details',
          errorCode: payload?.body?.errorCode || 0,
          data: null,
        },
      };

    case actionConstants.GET_DISPUTE_ORDER_DETAILS_V3_CLEAR:
      return {
        ...state,
        disputeOrderDetailsV3State,
      };

    // Get Dispute Order List V3
    case actionConstants.GET_DISPUTE_ORDER_LIST_V3_REQUEST:
      return {
        ...state,
        disputeOrderListV3State: {
          ...state.disputeOrderListV3State,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };

    case actionConstants.GET_DISPUTE_ORDER_LIST_V3_SUCCESS:
      return {
        ...state,
        disputeOrderListV3State: {
          ...state.disputeOrderListV3State,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: payload?.body || null,
        },
      };

    case actionConstants.GET_DISPUTE_ORDER_LIST_V3_FAILURE:
      return {
        ...state,
        disputeOrderListV3State: {
          ...state.disputeOrderListV3State,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorMessage:
            payload?.body?.message || 'Failed to fetch dispute order id',
          errorCode: payload?.body?.errorCode || 0,
          data: null,
        },
      };

    case actionConstants.GET_DISPUTE_ORDER_LIST_V3_CLEAR:
      return {
        ...state,
        disputeOrderListV3State,
      };

    // Get Dispute Document Config
    case actionConstants.GET_DISPUTE_DOCUMENT_CONFIG_REQUEST:
      return {
        ...state,
        disputeDocumentConfigState: {
          ...state.disputeDocumentConfigState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };

    case actionConstants.GET_DISPUTE_DOCUMENT_CONFIG_SUCCESS:
      return {
        ...state,
        disputeDocumentConfigState: {
          ...state.disputeDocumentConfigState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: payload?.body?.documentTypes || null,
        },
      };

    case actionConstants.GET_DISPUTE_DOCUMENT_CONFIG_FAILURE:
      return {
        ...state,
        disputeDocumentConfigState: {
          ...state.disputeDocumentConfigState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorMessage:
            payload?.body?.message || 'Failed to fetch dispute document config',
          errorCode: payload?.body?.errorCode || 0,
          data: null,
        },
      };

    // Dispute Filters State Management
    case actionConstants.SET_DISPUTE_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...payload,
        },
      };

    case actionConstants.CLEAR_DISPUTE_FILTERS:
      return {
        ...state,
        filters: { ...defaultFilters },
      };

    // Chargeback Defend Modal State Management
    case actionConstants.SET_CHARGEBACK_DEFEND_MODAL:
      return {
        ...state,
        chargebackDefendModal: {
          ...state.chargebackDefendModal,
          ...payload,
        },
      };

    case actionConstants.CLEAR_CHARGEBACK_DEFEND_MODAL:
      return {
        ...state,
        chargebackDefendModal: { 
          visible: false,
          type: '',
          continue: false
         },
        uploadDocumentState: {},
      };
    case actionConstants.UPLOAD_CHARGEBACK_DOCUMENT_SUCCESS:
      return {
        ...state,
        uploadDocumentState: {
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          ...payload.body,
        },
      };

    case actionConstants.UPLOAD_CHARGEBACK_DOCUMENT_FAILURE:
      return {
        ...state,
        uploadDocumentState: {
          ...state.uploadDocumentState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
        },
      };

    case actionConstants.UPLOAD_CHARGEBACK_DOCUMENT_REQUEST:
      return {
        ...state,
        uploadDocumentState: {
          ...state.uploadDocumentState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };

    case actionConstants.UPLOAD_CHARGEBACK_DOCUMENT_CLEAR:
      return {
        ...state,
        uploadDocumentState: {},
      };

    // Reupload Chargeback Document Cases
    case actionConstants.REUPLOAD_CHARGEBACK_DOCUMENT:
      return {
        ...state,
        reuploadDocumentState: {
          ...state.reuploadDocumentState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };

    case actionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_SUCCESS:
      return {
        ...state,
        reuploadDocumentState: {
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          ...payload,
        },
      };

    case actionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_FAILURE:
      return {
        ...state,
        reuploadDocumentState: {
          ...state.reuploadDocumentState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
        },
      };

    case actionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_REQUEST:
      return {
        ...state,
        reuploadDocumentState: {
          ...state.reuploadDocumentState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };

    case actionConstants.REUPLOAD_CHARGEBACK_DOCUMENT_CLEAR:
      return {
        ...state,
        reuploadDocumentState: {},
      };

    // Clear all dispute states on logout
    case actionConstants.POST_LOGOUT_SUCCESS:
      return {
        ...initialState, // Reset to initial state
      };
      
    default:
      return {
        ...state,
      };
  }
}
